import { useMutation } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import type { PaddleAccountStatus, PaddleCheckoutBootstrap } from "@shared/paddle-api";
import { apiRequest } from "@/lib/queryClient";
import { openPaddleCheckout } from "@/lib/paddleCheckout";
import type { MonthlyPaddleCheckoutRequest } from "@shared/paddle-checkout";

type Notice = { tone: "pending" | "error" | "success"; text: string };
export function usePaddleAccountCheckout(options: {
  requestId: () => string;
  refresh: (status?: PaddleAccountStatus) => Promise<void>;
  pending: (pending: boolean) => void;
  notice: (notice: Notice) => void;
}) {
  return useMutation({
    retry: false,
    mutationFn: async (tier: "pro" | "agent"): Promise<PaddleCheckoutBootstrap> => {
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios")
        throw Error("Paid checkout is not available inside the iOS app.");
      return (await apiRequest("/api/billing/checkout", {
        method: "POST", body: {
          tier, interval: "monthly", requestId: options.requestId(),
        } satisfies MonthlyPaddleCheckoutRequest,
      })).json();
    },
    onSuccess: async bootstrap => {
      options.pending(true);
      options.notice({ tone: "pending", text: "Review the monthly plan, applicable tax and any trial details in Paddle before subscribing." });
      let completed = false;
      try {
        await openPaddleCheckout(bootstrap, event => {
          if (event.name === "checkout.completed" && !completed) {
            completed = true;
            void apiRequest("/api/billing/checkout/confirm", {
              method: "POST", body: { transactionId: bootstrap.transactionId },
            }).then(response => response.json() as Promise<PaddleAccountStatus>)
              .then(async status => {
                await options.refresh(status);
                options.notice(status.providerState === "known"
                  ? { tone: "success", text: "Your monthly plan was verified with Paddle." }
                  : { tone: "pending", text: "Plan activation is not verified yet. Refresh billing status; do not submit another purchase." });
              }).catch(() => {
                void options.refresh();
                options.notice({ tone: "pending", text: "Payment or trial activation is not verified yet. Refresh billing status; do not submit another purchase." });
              }).finally(() => options.pending(false));
          } else if (event.name === "checkout.closed" && !completed) {
            options.pending(false);
            void options.refresh().then(() => {
              if (!completed) options.notice({ tone: "pending",
                text: "Checkout closed. Verify any submitted payment, or close the unpaid checkout in Account before choosing another plan." });
            }).catch(() => options.notice({ tone: "pending",
              text: "Checkout status is unknown. Refresh billing status before starting another purchase." }));
          }
        });
      } catch {
        options.pending(false);
        void options.refresh();
        options.notice({ tone: "error", text: "Checkout could not open. Check Account for an unfinished checkout before trying again." });
      }
    },
    onError: () => {
      void options.refresh();
      options.notice({ tone: "pending", text: "Checkout could not be confirmed. Check Account for an unfinished checkout before starting another purchase." });
    },
  });
}
